



























































import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import { SortOrder } from '@/jbi-shared/types/search.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { EntityTypes } from '@/store/modules/module-tree/enums/module-tree.enums';
import { PermissionsMatrixActionsEnum } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { isUserAllowed } from '@/utils/rbac.util';
import GroupLevelAttributeList from './components/GroupLevelAttributeList.vue';
import { navUtil } from '@/utils/nav.util';
import {
  FilteredGroupLevelAttributesPayload,
  GroupLevelAttributeType
} from '@/store/modules/admin/types/group-level-attribute.types';
import { GroupLevelAttribute } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { Pagination } from '@/store/modules/admin/types/admin.types';
import CreateNewGroupLevelAttributeModal from '../AdminGroupSettings/components/GroupLevelAttributes/CreateNewGroupLevelAttributeModal.vue';

@Component({
  computed: {
    PermissionsMatrixActionsEnum() {
      return PermissionsMatrixActionsEnum;
    }
  },
  components: {
    BasePaginatorHoc
  }
})
export default class GroupLevelAttributeTab extends Vue {
  public perPage: number = 50;
  public page: number = 1;
  public sortColumn: string = 'attributeLabel';
  public sortOrder: SortOrder = SortOrder.ASC;
  public filterInput: string = '';

  @Action('admin/getPaginatedGroupLevelAttributes')
  public getPaginatedGroupLevelAttributes!: (
    params: FilteredGroupLevelAttributesPayload
  ) => void;

  @State(({ admin }: RootState) => admin.paginatedGroupLevelAttributes)
  public paginatedGroupLevelAttributes!: Pagination<GroupLevelAttribute>;

  @State(
    ({ admin }: RootState) => admin.apiState.getPaginatedGroupLevelAttributes
  )
  public getPaginatedGroupLevelAttributesStatus!: ApiState;

  @State((state: RootState) => state.rbac.groupTypesUserHasAccessTo)
  public groupTypesUserHasAccessTo!: string[];

  @State((state: RootState) => state.rbac.groupsUserHasAccessTo)
  public groupsUserHasAccessTo!: number[];

  @State(({ admin }: RootState) => admin.groupLevelAttributeTypes)
  allAttributeTypes!: GroupLevelAttributeType[] | undefined;

  @Action('admin/getGroupLevelAttributeTypes')
  getGroupLevelAttributeTypes!: () => void;

  get GroupLevelAttributeList(): typeof GroupLevelAttributeList {
    return GroupLevelAttributeList;
  }

  get isGroupViewAllowed() {
    return this.checkForPermissions(
      PermissionsMatrixActionsEnum.READ,
      'group_administration-groups-read_groups',
      true
    );
  }

  public checkForPermissions(
    action: PermissionsMatrixActionsEnum,
    module: string,
    skipImplicitCheck?: boolean
  ): boolean {
    const groupTypeName = this.groupTypesUserHasAccessTo
      ? this.groupTypesUserHasAccessTo
      : undefined;
    const groupId = this.groupsUserHasAccessTo
      ? this.groupsUserHasAccessTo
      : undefined;
    return isUserAllowed(
      action,
      module,
      EntityTypes.GROUP,
      groupTypeName,
      groupId,
      undefined,
      skipImplicitCheck
    );
  }

  public handleSort(sortCriteria: {
    sortColumn: string;
    sortOrder: SortOrder.ASC;
  }): void {
    this.sortColumn = sortCriteria.sortColumn;
    this.sortOrder = sortCriteria.sortOrder;

    const params = {
      attributeLabel:
        (this.$route.query.attributeLabel as string) || this.filterInput,
      limit: +this.$route.query.limit || this.perPage,
      page: +this.$route.query.page || this.page,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    };

    this.getPaginatedGroupLevelAttributes(params);
  }

  public handlePaginator({
    perPage,
    page
  }: {
    perPage: number;
    page: number;
  }): void {
    if (this.$route.query.tab !== 'GroupAttributes') {
      return;
    }
    this.perPage = perPage || +this.$route.query.limit;
    this.page = page || +this.$route.query.page;

    const params = {
      attributeLabel:
        (this.$route.query.attributeLabel as string) || this.filterInput,
      limit: this.perPage,
      page: this.page,
      sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
      sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
    };

    this.getPaginatedGroupLevelAttributes(params);
  }

  get filterPayload(): FilteredGroupLevelAttributesPayload {
    return {
      attributeLabel: this.filterInput,
      limit: +this.$route.query.limit || this.perPage,
      page: +this.$route.query.page || this.page,
      sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
      sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
    };
  }

  @Debounce(600)
  async fetchGroupLevelAttributeList() {
    await this.getPaginatedGroupLevelAttributes(this.filterPayload);
  }

  @Watch('filterInput')
  onFilterInputChange() {
    this.fetchGroupLevelAttributeList();
  }

  public handleCreateAttribute(): void {
    this.$buefy.modal.open({
      parent: this,
      component: CreateNewGroupLevelAttributeModal,
      hasModalCard: true,
      trapFocus: true,
      fullScreen: false,
      canCancel: true,
      props: {
        allAttributeTypes: this.allAttributeTypes,
        showRequiredToggle: false // Hide the Required toggle in the administration context
      },
      events: {
        addNewAttribute: () => this.routeParam()
      }
    });
  }

  public mounted(): void {
    // Pre-fetch attribute types so the dropdown is not empty when the creation modal opens
    this.getGroupLevelAttributeTypes();

    if (this.$route.query.tab === 'GroupAttributes') {
      navUtil.ignoreDuplicateNavigation(
        this.$router.push({
          path: this.$route.path,
          query: {
            tab: 'GroupAttributes',
            attributeLabel:
              (this.$route.query.attributeLabel as string) || this.filterInput,
            limit:
              (this.$route.query.limit as string) || this.perPage.toString(),
            page: (this.$route.query.page as string) || this.page.toString(),
            sortColumn:
              (this.$route.query.sortColumn as string) || this.sortColumn,
            sortOrder:
              (this.$route.query.sortOrder as SortOrder) || this.sortOrder
          }
        })
      );
      this.perPage = +this.$route.query.limit || this.perPage;
      this.page = +this.$route.query.page || this.page;
      this.filterInput =
        (this.$route.query.attributeLabel as string) || this.filterInput;

      this.handlePaginator({ perPage: this.perPage, page: this.page });
    }
  }

  @Watch('getPaginatedGroupLevelAttributesStatus.success')
  @isDifferent
  @isTruthy
  public async getGroupLevelAttributeListSuccess() {
    this.$emit('activateTab');

    await navUtil.ignoreDuplicateNavigation(
      this.$router.push({
        path: this.$route.path,
        query: {
          tab: 'GroupAttributes',
          attributeLabel: this.filterInput,
          limit: this.perPage.toString(),
          page: this.page.toString(),
          sortColumn: this.sortColumn,
          sortOrder: this.sortOrder
        }
      })
    );

    this.$nextTick(() => {
      const searchInput = this.$refs.searchInput as any;
      if (searchInput?.$el) {
        searchInput.$el.querySelector('input').focus();
      }
    });
  }

  @Watch('getPaginatedGroupLevelAttributesStatus.error')
  @isDifferent
  @isTruthy
  public getGroupLevelAttributeListError() {
    this.$emit('activateTab');
  }

  @Watch('$route.query.tab')
  public routeParam() {
    if (this.$route.query.tab === 'GroupAttributes') {
      this.handlePaginator({ perPage: this.perPage, page: this.page });
    }
  }
}
